import { getAccessToken } from '../authentication/supabaseService';
import type { CostBundle } from '../../components/pages/admin/setupScoreCostMetrics';

const API_BASE = '/api/admin';

// ── Types (mirror server/domains/setupScore/setupScoreService.ts) ──

export interface SupplierSetup {
  configured: number;
  activeLast7Days: number;
}

export interface SetupMetric {
  category: { assignedItems: number; totalItems: number };
  cost: { linkedItems: number; totalItems: number };
  /**
   * food/drinks: cost-only % for that subset. overall: 50% category% (all dishes) +
   * 50% cost% (food+drinks dishes only). Null when no active dishes.
   */
  score: number | null;
}

export interface RestaurantSetupScore {
  restaurantId: string;
  restaurantName: string;
  relevant: boolean;
  suppliers: SupplierSetup;
  flags: { hasRecipes: boolean; hasInventory: boolean };
  overall: SetupMetric;
  food: SetupMetric;
  drinks: SetupMetric;
}

export interface ClientSetupScore {
  clientId: string;
  clientName: string | null;
  clientEmail: string;
  restaurants: RestaurantSetupScore[];
}

export interface SetupScoreAggregate {
  totalRestaurants: number;
  relevantRestaurants: number;
  scoredRestaurants: number;
  avgScore: number | null;
  foodAvgScore: number | null;
  drinksAvgScore: number | null;
  categoryAssigned: number;
  categoryTotal: number;
  costLinked: number;
  costTotal: number;
  fullyComplete: number;
}

export interface SetupScoreResponse {
  generatedAt: string;
  windows: { salesDays: number };
  aggregate: SetupScoreAggregate;
  clients: ClientSetupScore[];
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  return response.json();
}

export async function getSetupScore(): Promise<SetupScoreResponse> {
  return fetchWithAuth('/setup-score');
}

/** Raw per-restaurant cost-price bundle; the frontend runs the real cost calc on it. */
export async function getCostBundle(restaurantId: string): Promise<CostBundle> {
  return fetchWithAuth(`/setup-score/cost-bundle/${restaurantId}`);
}

/** Toggle whether a restaurant counts toward the setup-score aggregate (shared by all super-admins). */
export async function setRestaurantRelevance(restaurantId: string, relevant: boolean): Promise<void> {
  await fetchWithAuth('/setup-score/relevance', {
    method: 'PUT',
    body: JSON.stringify({ restaurantId, relevant }),
  });
}
